// Pure decision logic for gating a Step-2 service-alert popup on discount
// validation / Discount Loading state. Extracted from
// useServiceAlertsByCountryCodeObserver so the behaviour can be unit-tested
// without the observer's store/hook coupling.
//
// Two rules this encodes (both were runtime-diagnosed defects):
//   1. Defer while discounts are busy AND on the input-change tick (the busy
//      flag propagates from Remittance one render late); flush the deferred
//      show only on the busy -> settled transition (race-free).
//   2. Gating applies to DELAY (informational) alerts only. DISABLE (blocking)
//      alerts are never deferred or force-re-shown.

export interface SlotGateInput {
  // The DiscountLoadingProvider is mounted (Step 2 ver2). False on pages
  // without it (base page keeps the prior "re-show on input change").
  isGatingActive: boolean;
  // Discounts are validating/loading (only meaningful when gating is active).
  isDiscountBusy: boolean;
  // Discounts transitioned busy -> settled on this observer run.
  isDiscountJustSettled: boolean;
  // A real amount input event for this slot occurred since the previous
  // observer run, reported by the fee-fetch completion callback via
  // DiscountLoadingContext. This is an *event*, not a state comparison: see the
  // isInputChanged note below for why the comparison cannot see it.
  isAmountInputEvent: boolean;
  // A genuine user change to a non-empty amount/currency for this slot. Drives
  // the deferral and the range-level (amount-dependent) re-show.
  //
  // This is a *sampler*, not an observer of the input event: the amount reaches
  // the store only from the debounced fee-fetch completion callback, so an
  // edit-and-restore that lands inside one debounce window commits the value it
  // started from and reads here as no change at all. isAmountInputEvent is the
  // signal for exactly that case.
  isInputChanged: boolean;
  // The slot's currency code changed. Drives the currency-level re-show only:
  // a currency-level alert depends on the currency, not the amount, so an
  // amount-only change must not re-show an already-dismissed currency alert.
  isCurrencyChanged: boolean;
  // The slot's receiving amount is > 0.
  hasAmount: boolean;
  // Prior per-slot "a range/input re-show is owed once discounts settle" flag.
  pendingShow: boolean;
  // Prior per-slot "a currency-level re-show is owed once discounts settle" flag.
  pendingShowCurrency: boolean;
}

export interface SlotGateResult {
  // Discount-driven suppression for this slot, before the per-alert-type gate
  // and before OR-ing with the existing non-discount prevent reasons.
  deferByDiscountRaw: boolean;
  // Range-level (amount-dependent) re-show for this slot, before the
  // per-alert-type gate.
  baseForceReshow: boolean;
  // Currency-level re-show for this slot, before the per-alert-type gate.
  baseForceReshowCurrency: boolean;
  // The per-slot range/input pending flag to carry into the next observer run.
  nextPendingShow: boolean;
  // The per-slot currency-level pending flag to carry into the next run.
  nextPendingShowCurrency: boolean;
}

// Slot-level decision (shared by a currency listener and its range listener).
export const computeSlotDiscountGate = ({
  isGatingActive,
  isDiscountBusy,
  isDiscountJustSettled,
  isAmountInputEvent,
  isInputChanged,
  isCurrencyChanged,
  hasAmount,
  pendingShow,
  pendingShowCurrency,
}: SlotGateInput): SlotGateResult => {
  if (!isGatingActive) {
    // Base page / no provider: preserve prior "re-show on input change" for the
    // range track and the currency track alike.
    return {
      deferByDiscountRaw: false,
      baseForceReshow: isInputChanged,
      baseForceReshowCurrency: isInputChanged,
      nextPendingShow: pendingShow,
      nextPendingShowCurrency: pendingShowCurrency,
    };
  }

  // Range/input track: an amount-or-currency change owes a re-show once
  // discounts settle; carry any prior owed show.
  //
  // A reported amount input event owes one too, and covers what the comparison
  // above cannot see: the rapid round trip (remove a digit, retype it inside
  // one debounce window) commits the value it already held. The alert data is
  // then unchanged in a populated slot, so the listener reaches no branch, no
  // onCheck runs, and onSuppressedShow (the other way a debt gets owed) never
  // fires either. Without this the settle that follows has nothing to flush and
  // the dismissed DELAY never returns.
  //
  // An earlier version of this rule used the discount settled -> busy edge as
  // the stand-in. That was wrong in a way worth recording: a discount cycle
  // also starts when Remittance remounts, so stepping away and back re-showed
  // an alert the user had dismissed without touching anything. Only an event
  // raised by the input itself distinguishes the two -- discount state cannot,
  // because the same cycle has several causes.
  //
  // Requires hasAmount for the same reason the deferral does: with no amount
  // discounts never settle, so an owed show would never be flushed.
  //
  // Scoped to this track deliberately. The currency track keeps the stricter
  // deferredCurrencyShow rule below, so a dismissed currency-level alert does
  // not re-pop on a cycle that changed only the amount.
  const effectivePending =
    isInputChanged || (isAmountInputEvent && hasAmount) ? true : pendingShow;
  const baseForceReshow =
    isDiscountJustSettled && effectivePending && hasAmount;

  // Only defer when the slot has an amount. Discount validation is
  // amount-driven, so with no amount entered discounts never settle
  // (isDiscountSettled stays false / isDiscountBusy stays true); deferring then
  // would suppress the initial currency-level DELAY show indefinitely, because
  // the busy -> settled flush never fires. With no amount there is nothing to
  // wait for, so show immediately (first Step-2 access).
  const deferByDiscountRaw = (isDiscountBusy || isInputChanged) && hasAmount;

  // Currency track: a currency-level re-show is owed only when a currency
  // change was actually *deferred* by discount-loading. Setting it merely on
  // isCurrencyChanged would wrongly re-show after an amount entry: the initial
  // no-amount show already fired via onGet (never deferred), so nothing is
  // owed. Gating the owed show on deferByDiscountRaw ties it to a real
  // deferral, and the initial null -> code selection (when an amount is
  // present) still flushes a genuinely deferred first show.
  const deferredCurrencyShow = deferByDiscountRaw && isCurrencyChanged;
  const effectivePendingCurrency = deferredCurrencyShow
    ? true
    : pendingShowCurrency;
  const baseForceReshowCurrency =
    isDiscountJustSettled && effectivePendingCurrency && hasAmount;

  return {
    deferByDiscountRaw,
    baseForceReshow,
    baseForceReshowCurrency,
    // Clear each owed show once it is flushed; otherwise carry it forward.
    nextPendingShow: baseForceReshow ? false : effectivePending,
    nextPendingShowCurrency: baseForceReshowCurrency
      ? false
      : effectivePendingCurrency,
  };
};

export interface ListenerGateInput {
  isGatingActive: boolean;
  // This listener's alert is a DELAY (informational) alert.
  isDelayAlert: boolean;
  deferByDiscountRaw: boolean;
  baseForceReshow: boolean;
}

export interface ListenerGateResult {
  // Discount-driven contribution to isPreventShowServiceAlertsModal.
  deferByDiscount: boolean;
  // Final isForceReshow for this listener.
  forceReshow: boolean;
}

// Per-listener gate: applies the DELAY-only scoping. A DISABLE alert is never
// deferred or force-re-shown, so its modal and reset behave as they did before
// this feature.
export const applyServiceAlertDiscountGate = ({
  isGatingActive,
  isDelayAlert,
  deferByDiscountRaw,
  baseForceReshow,
}: ListenerGateInput): ListenerGateResult => {
  if (!isGatingActive) {
    return { deferByDiscount: false, forceReshow: baseForceReshow };
  }

  return {
    deferByDiscount: deferByDiscountRaw && isDelayAlert,
    forceReshow: baseForceReshow && isDelayAlert,
  };
};

export interface RangeDisableGateInput {
  isGatingActive: boolean;
  // This listener's alert is a DISABLE (blocking) alert.
  isDisableAlert: boolean;
  deferByDiscountRaw: boolean;
}

// Whether to skip a range listener call outright, rather than call it with the
// modal suppressed.
//
// A range DISABLE cannot use the DELAY deferral above, and deferring only its
// modal is a known defect: the store write is *actuating*, not inert. The
// Remittance form reset keys on the store slot plus "the modal is not showing"
// (FormInputAmountValidation), and that second term is also true *before* the
// modal has ever shown -- so a recorded-but-unshown DISABLE silently clears the
// send and receiving amounts with no explanation. isPreventNextStep2 reads the
// same slot. Suppressing the modal alone therefore hides the cause while the
// effect still fires.
//
// Skipping the whole call keeps the store write, the modal and the reset
// atomic: nothing is recorded while the amount/discount flow is unsettled, and
// once deferByDiscountRaw clears, the ordinary onGet path delivers all three
// together (a range DISABLE never sets isPreventOnGet, so no force-reshow is
// needed and force-reshow stays DELAY-only).
//
// Note this is the opposite requirement to a range DELAY, whose record must
// happen even when its popup is suppressed -- a DELAY slot is display-only,
// and skipping its record leaves it permanently unshowable. Do not unify them.
export const shouldDeferRangeDisableEvaluation = ({
  isGatingActive,
  isDisableAlert,
  deferByDiscountRaw,
}: RangeDisableGateInput): boolean =>
  isGatingActive && isDisableAlert && deferByDiscountRaw;
